/**
 * Zelfevaluatie op kerntaken (Epic 4 — Story 4.5, AC #1, AD-12)
 *
 * v2-tegenhanger van het taken-lijst-gedeelte in `SelfEvaluationPage.tsx`:
 * scoort per kerntaak (met vaardigheden als ankers, UX-DR4) i.p.v. per oude
 * taak. Hergebruikt `KerntaakScoreCard` (Story 4.2/4.3) voor consistentie
 * met de beoordelaar-kaart — inclusief ankers; het punteneffect-getal is
 * hier puur informatief (zelfevaluatie telt niet mee in de officiële score).
 * `toelichting` wordt bewust NIET opgeslagen (AC #0: `core_task_self_evaluations`
 * heeft geen toelichting-kolom) — lokaal UI-only veld, niet verzonden.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useEvaluation,
  useCoreTaskSelfEvaluation,
  useSaveCoreTaskSelfEvaluation,
  useSubmitCoreTaskSelfEvaluation,
} from './useEvaluations';
import { useTaskPeriods } from '../employees/useAssignmentPeriods';
import { useCoreTasks } from '../settings/useCoreTasks';
import { KerntaakScoreCard } from './wizard/KerntaakScoreCard';
import { resolvePeriodsAndWeights } from '@functieloonhuis/shared';
import type { AssignmentPeriodDTO } from '@functieloonhuis/shared';
import type { CoreTaskSelfEvaluationScore } from '../../lib/evaluationsApi';

interface CoreTaskSelfEvaluationViewProps {
  evaluationId: number;
  onSubmitted: () => void;
}

interface LocalScore {
  periodId: number;
  coreTaskId: number;
  score: number | null;
}

export function CoreTaskSelfEvaluationView({ evaluationId, onSubmitted }: CoreTaskSelfEvaluationViewProps) {
  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  const { data: selfEvalData, isLoading: isLoadingSelfEval } = useCoreTaskSelfEvaluation(evaluationId);
  const { data: allPeriods, isLoading: isLoadingPeriods } = useTaskPeriods(evaluation?.employeeId);
  const { data: catalog, isLoading: isLoadingCatalog } = useCoreTasks();

  const saveSelfEval = useSaveCoreTaskSelfEvaluation();
  const submitSelfEval = useSubmitCoreTaskSelfEvaluation();

  const [localScores, setLocalScores] = useState<LocalScore[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const evaluationWindow = useMemo(
    () => (evaluation ? { start: evaluation.periodeStart, end: evaluation.periodeEnd } : null),
    [evaluation]
  );

  const coveredPeriods: AssignmentPeriodDTO[] = useMemo(() => {
    if (!evaluationWindow || !allPeriods) return [];
    const { weights } = resolvePeriodsAndWeights(
      evaluationWindow,
      allPeriods.map((p) => ({ id: p.id, startDate: p.startDate, endDate: p.endDate }))
    );
    const coveredIds = new Set(weights.map((w) => w.periodId));
    return allPeriods.filter((p) => coveredIds.has(p.id)).sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [evaluationWindow, allPeriods]);

  useEffect(() => {
    if (coveredPeriods.length === 0) return;
    const existing = selfEvalData?.scores ?? [];
    const initial: LocalScore[] = [];
    for (const period of coveredPeriods) {
      for (const assignment of period.assignments) {
        const found = existing.find((s) => s.periodId === period.id && s.coreTaskId === assignment.coreTaskId);
        initial.push({ periodId: period.id, coreTaskId: assignment.coreTaskId, score: found?.score ?? null });
      }
    }
    setLocalScores(initial);
  }, [coveredPeriods, selfEvalData]);

  const skillsByCoreTaskId = useMemo(() => {
    const map = new Map<number, string[]>();
    catalog?.forEach((task) => map.set(task.id, task.skills.map((s) => s.label)));
    return map;
  }, [catalog]);

  const isCompleted = evaluation?.selfEvaluationAt != null;
  const completedCount = localScores.filter((s) => s.score !== null).length;
  const totalCount = localScores.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleScoreChange = (periodId: number, coreTaskId: number, score: number) => {
    setLocalScores((prev) =>
      prev.map((s) => (s.periodId === periodId && s.coreTaskId === coreTaskId ? { ...s, score } : s))
    );
    setHasUnsavedChanges(true);
  };

  const toScorePayload = (): CoreTaskSelfEvaluationScore[] =>
    localScores
      .filter((s) => s.score !== null)
      .map((s) => ({ periodId: s.periodId, coreTaskId: s.coreTaskId, score: s.score! }));

  const handleSave = async () => {
    const payload = toScorePayload();
    if (payload.length === 0) {
      alert('Vul minimaal één score in voordat je opslaat.');
      return;
    }
    try {
      await saveSelfEval.mutateAsync({ evaluationId, scores: payload });
      setHasUnsavedChanges(false);
    } catch {
      // Error handled by mutation state
    }
  };

  const handleSubmit = async () => {
    const unscored = localScores.filter((s) => s.score === null);
    if (unscored.length > 0) {
      alert(`Je hebt nog ${unscored.length} kerntaak/kerntaken niet beoordeeld.`);
      return;
    }
    try {
      await saveSelfEval.mutateAsync({ evaluationId, scores: toScorePayload() });
      await submitSelfEval.mutateAsync(evaluationId);
      onSubmitted();
    } catch {
      // Error handled by mutation state
    }
  };

  const isLoading = isLoadingEval || isLoadingSelfEval || isLoadingPeriods || isLoadingCatalog;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5A7A]"></div>
        <span className="ml-3 text-gray-600">Kerntaken laden...</span>
      </div>
    );
  }

  return (
    <div data-testid="core-task-self-evaluation-view" className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Zelf-evaluatie op kerntaken</h1>
        <p className="mt-1 text-sm text-gray-500">Beoordeel jezelf op je toegewezen kerntaken.</p>
      </div>

      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Voortgang</span>
          <span className="text-sm text-gray-500">{completedCount} van {totalCount} kerntaken beoordeeld</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div className="bg-[#2F5A7A] h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      {isCompleted && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">Je zelf-evaluatie is ingediend.</p>
        </div>
      )}

      <div className="space-y-4 mb-6">
        {coveredPeriods.map((period) =>
          period.assignments.map((assignment) => {
            const localScore = localScores.find(
              (s) => s.periodId === period.id && s.coreTaskId === assignment.coreTaskId
            );
            const skills = skillsByCoreTaskId.get(assignment.coreTaskId) ?? [];
            const earnedPoints =
              localScore?.score != null ? 50 * (assignment.timePercentage / 100) * (localScore.score / 5) : 0;

            return (
              <KerntaakScoreCard
                key={`${period.id}-${assignment.coreTaskId}`}
                coreTaskId={assignment.coreTaskId}
                coreTaskName={assignment.coreTaskName}
                timePercentage={assignment.timePercentage}
                skills={skills}
                score={localScore?.score ?? null}
                toelichting=""
                earnedPointsWithinPeriod={earnedPoints}
                onScoreChange={(score) => handleScoreChange(period.id, assignment.coreTaskId, score)}
                onToelichtingChange={() => {
                  /* geen toelichting-kolom voor zelfevaluatie op kerntaken (AC #0) */
                }}
              />
            );
          })
        )}
      </div>

      {!isCompleted && (
        <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={handleSave}
              disabled={saveSelfEval.isPending || !hasUnsavedChanges}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {saveSelfEval.isPending ? 'Opslaan...' : 'Tussentijds Opslaan'}
            </button>
            {hasUnsavedChanges && <span className="text-sm text-yellow-600">Niet-opgeslagen wijzigingen</span>}
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitSelfEval.isPending || completedCount < totalCount}
            className="px-6 py-2 bg-[#2F5A7A] text-white rounded-lg hover:bg-[#2F5A7A]/90 disabled:opacity-50"
          >
            {submitSelfEval.isPending ? 'Indienen...' : 'Zelf-evaluatie Indienen'}
          </button>
        </div>
      )}
    </div>
  );
}
